import React, { useEffect, useRef, useState, useCallback, ChangeEvent } from 'react';
import "./CameraVideo.css";
import ReactLoading from "react-loading";
import { useDispatch } from 'react-redux';
import { addImage } from '../../store/features/imagesDataSlice';
import { getImgFileName } from '../../utils/helpers';
import { VideoRef, SelectRef } from '../../utils/typesAndMaps';
import { updateState } from '../../store/features/generalStatesSlice';

export default function CameraVideo() {
  const VideoRef: VideoRef = useRef(null);
  const resolutionRef: SelectRef = useRef(null);
  const [cameraList, setCameraList] = useState([] as Array<MediaDeviceInfo>);
  const [resolutionList, setResolutionList] = useState([
    { width: 640, height: 480 },
    { width: 640, height: 480 },
    { width: 1280, height: 720 },
    { width: 1920, height: 1080 },
    { width: 3840, height: 2160 }
  ]);
  const [isLoadend, setLoadend] = useState(false);
  const [isShowLoading, setShowLoading] = useState(false);
  const [currentDeviceId, setCurrentDeviceId] = useState("");
  const dispatch = useDispatch();

  const closeCameraWindow = useCallback(() => {
    dispatch(updateState({ stateName: "cameraState", value: false }));
    stopVideo();
  }, [dispatch])

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((deviceList) => {
      const cameraList: Array<MediaDeviceInfo> = [];
      deviceList.forEach((item) => {
        if (item.kind === "videoinput") {
          cameraList.push(item);
        }
      })
      setCurrentDeviceId(cameraList[0].deviceId);
      setCameraList(cameraList);
      navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: cameraList[0].deviceId ? cameraList[0].deviceId : undefined }, width: 640, height: 480 } }).then((mediaStream: MediaStream) => {
        VideoRef.current!.srcObject = mediaStream;
        VideoRef.current!.onloadedmetadata = function () {
          VideoRef.current!.play().then(() => {
            setLoadend(true);
            setShowLoading(true);
          });
        };
      }).catch((ex) => {
        alert('Play video failed: ' + (ex.message || ex));
        closeCameraWindow();
      });
    });
  }, [closeCameraWindow])

  const stopVideo = () => {
    if (VideoRef.current!.srcObject) {
      (VideoRef.current!.srcObject as MediaStream).getTracks().forEach(function (track) {
        track.stop();
      })
    }
  }

  const changeCamera = (e: ChangeEvent<HTMLSelectElement>) => {
    stopVideo();
    setShowLoading(false);
    const deviceId = e.target.value;
    const constraints = { video: { deviceId: { exact: deviceId }, width: resolutionList[0].width, height: resolutionList[0].height } };
    playVideo(constraints, true, deviceId);
  }

  const changeResolution = (e: ChangeEvent<HTMLSelectElement>) => {
    stopVideo();
    setShowLoading(false);
    const value = parseInt(e.target.value);
    const constraints = { video: { deviceId: { exact: currentDeviceId }, width: resolutionList[value].width, height: resolutionList[value].height } };
    playVideo(constraints);
  }

  const playVideo = (constraints: MediaStreamConstraints, bNeedSaveDeviceId?: boolean, deviceId?: string) => {
    navigator.mediaDevices.getUserMedia(constraints).then((mediaStream: MediaStream) => {
      VideoRef.current!.srcObject = mediaStream;
      VideoRef.current!.onloadedmetadata = function () {
        VideoRef.current!.play().then(() => {
          if (bNeedSaveDeviceId) setCurrentDeviceId(deviceId as string);
          resolutionList[0].width = VideoRef.current!.videoWidth;
          resolutionList[0].height = VideoRef.current!.videoHeight;
          (resolutionRef.current!.children[0] as HTMLOptionElement).selected = true;
          setResolutionList([...resolutionList]);
          setShowLoading(true);
        })
      };
    }).catch((ex) => {
      alert('Play video failed: ' + (ex.message || ex));
      closeCameraWindow();
    });
  }

  const renderCameraList = () => {
    return cameraList.map((item, index) => {
      return (
        <option key={index} value={item.deviceId}>{item.label ? item.label : `camera ${index + 1}`}</option>
      )
    })
  }

  const renderResolutionList = () => {
    return resolutionList.map((item, index) => {
      return (
        <option key={index} value={index}>{`${index ? "ask" : ""} ${item.width}x${item.height}`}</option>
      )
    })
  }

  const captureAndClose = () => {
    const cvs = document.createElement("canvas");
    cvs.width = VideoRef.current!.videoWidth;
    cvs.height = VideoRef.current!.videoHeight;
    const ctx = cvs.getContext("2d")!;
    ctx.drawImage(VideoRef.current as HTMLVideoElement, 0, 0);
    const url = cvs.toDataURL("image/png");
    const imgData = { name: `${getImgFileName()}.png`, url, uploadState: false, sampleImg: false };
    dispatch(addImage([imgData]));
    closeCameraWindow();
  }

  return (
    <div className="dbr-camera-video">
      <div className="dbr-camera-loading" style={{ display: isShowLoading ? "none" : "block" }}><ReactLoading type="spinningBubbles"></ReactLoading></div>
      <video className="dbr-video" ref={VideoRef}></video>
      <select className="dbr-camera-select" style={{ display: isLoadend ? "block" : "none" }} onChange={changeCamera}>
        {renderCameraList()}
      </select>
      <select className="dbr-resolution-select" ref={resolutionRef} style={{ display: isLoadend ? "block" : "none" }} onChange={changeResolution}>
        {renderResolutionList()}
      </select>
      <button className="dbr-close-camera" onClick={closeCameraWindow} style={{ display: isLoadend ? "block" : "none" }}>
        <svg width="48" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M1490 1322q0 40-28 68l-136 136q-28 28-68 28t-68-28l-294-294-294 294q-28 28-68 28t-68-28l-136-136q-28-28-28-68t28-68l294-294-294-294q-28-28-28-68t28-68l136-136q28-28 68-28t68 28l294 294 294-294q28-28 68-28t68 28l136 136q28 28 28 68t-28 68l-294 294 294 294q28 28 28 68z"></path></svg>
      </button>
      <button className="dbr-capture" onClick={captureAndClose} style={{ display: isLoadend ? "block" : "none" }}>
        <svg width="48" viewBox="0 0 2048 1792" xmlns="http://www.w3.org/2000/svg"><path d="M1024 672q119 0 203.5 84.5t84.5 203.5-84.5 203.5-203.5 84.5-203.5-84.5-84.5-203.5 84.5-203.5 203.5-84.5zm704-416q106 0 181 75t75 181v896q0 106-75 181t-181 75h-1408q-106 0-181-75t-75-181v-896q0-106 75-181t181-75h224l51-136q19-49 69.5-84.5t103.5-35.5h512q53 0 103.5 35.5t69.5 84.5l51 136h224zm-704 1152q185 0 316.5-131.5t131.5-316.5-131.5-316.5-316.5-131.5-316.5 131.5-131.5 316.5 131.5 316.5 316.5 131.5z"></path></svg>
      </button>
    </div>
  );
}
